// import React, { useEffect, useState } from "react";
// import { Card, CardContent, Typography, Box } from "@mui/material";
// import { Work, BarChart, Store, Group, Warning ,People,MonetizationOn,Receipt} from "@mui/icons-material";
// import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

// interface HeaderCardProps {
//   title: string;
//   value: string;
//   percentage: string;
//   icon: "Work" | "BarChart" | "Store" | "Group" | "Warning" | "People" | "MonetizationOn" | "Receipt" | "CurrencyRupeeIcon" ;
//   onClick?: () => void; // Add this line
// }

// const icons = { Work, BarChart, Store, Group, Warning ,People,MonetizationOn,Receipt,CurrencyRupeeIcon};

// const HeaderCard: React.FC<HeaderCardProps> = ({ title, value, percentage, icon, onClick }) => {
//   const IconComponent = icons[icon];
//   const [displayValue, setDisplayValue] = useState(0);
//   const targetValue = parseInt(value.replace(/,/g, ''));

//   useEffect(() => {
//     let startValue = 0;
//     const duration = 2000; // duration in milliseconds
//     const increment = targetValue / (duration / 16); // assuming 60fps, so 16ms per frame

//     const timer = setInterval(() => {
//       startValue += increment;
//       if (startValue >= targetValue) {
//         startValue = targetValue;
//         clearInterval(timer);
//       }
//       setDisplayValue(Math.floor(startValue));
//     }, 16);

//     return () => clearInterval(timer);
//   }, [targetValue]);

//   return (
//     <Card
//       onClick={onClick}
//       style={{ cursor: "pointer" }}
//       onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
//       onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "white")}
//     >
//       <CardContent>
//         <Box display="flex" alignItems="center">
//           <Box mr={2}>
//             <IconComponent fontSize="large" />
//           </Box>
//           <div>
//             <Typography
//               variant="h6"
//               sx={{
//                 transition: "color 0.1s ease-out",
//                 "&:hover": {
//                   color: "#1976d2",
//                   fontWeight: "bold",
                  
//                 },
//               }}
//             >
//               {title}
//             </Typography>
//             <Typography variant="h4" fontWeight="bold">
//               {displayValue.toLocaleString()}
//             </Typography>
//             <Typography color="textSecondary">{percentage}</Typography>
//           </div>
//         </Box>
//       </CardContent>
//     </Card>
//   );
// };

// export default HeaderCard;

import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box, Tooltip } from "@mui/material";
import { Work, BarChart, Store, Group, Warning, People, MonetizationOn, Receipt } from "@mui/icons-material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

interface HeaderCardProps {
  title: string;
  value: string;
  percentage: string;
  icon: "Work" | "BarChart" | "Store" | "Group" | "Warning" | "People" | "MonetizationOn" | "Receipt" | "CurrencyRupeeIcon";
  onClick?: () => void;
  /** NEW: optional symbol shown before the number, e.g. "₹" for money cards.
   *  Leave undefined for plain counts (Total Products, Low Stock, etc). */
  prefix?: string;
}

const icons = { Work, BarChart, Store, Group, Warning, People, MonetizationOn, Receipt, CurrencyRupeeIcon };

// ── NEW: Indian-numbering compact format (Lakh/Crore) for large values, so
// a 7-8 digit rupee amount never has to render at full width on a small
// mobile card. Small counts (21, 199, 5...) pass through untouched. ────────
const formatCompact = (n: number): string => {
  if (n >= 10000000) return `${(n / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(2).replace(/\.00$/, "")} L`;
  if (n >= 1000) return n.toLocaleString("en-IN");
  return n.toString();
};

const HeaderCard: React.FC<HeaderCardProps> = ({ title, value, percentage, icon, onClick, prefix }) => {
  const IconComponent = icons[icon];
  const [displayValue, setDisplayValue] = useState(0);

  // NEW: robust parsing — strips ANY non-numeric characters (₹, commas,
  // letters like "L"/"Cr" if ever passed in) instead of only commas, so this
  // can never silently produce NaN regardless of how the caller formats the
  // incoming string. Falls back to 0 rather than crashing the count-up timer.
  const targetValue = (() => {
    const cleaned = String(value ?? "").replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  })();

  useEffect(() => {
    let startValue = 0;
    const duration = 2000;
    const increment = targetValue / (duration / 16);

    const timer = setInterval(() => {
      startValue += increment;
      if (startValue >= targetValue) {
        startValue = targetValue;
        clearInterval(timer);
      }
      setDisplayValue(Math.floor(startValue));
    }, 16);

    return () => clearInterval(timer);
  }, [targetValue]);

  const displayText = formatCompact(displayValue);
  const fullExactText = formatCompact(targetValue); // shown in tooltip, exact final value

  return (
    <Card
      onClick={onClick}
      // NEW: fill the full height the Grid row gives it, and lay content
      // out in a column so the value block sits at a consistent position
      // regardless of how many lines the title takes.
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 0.15s ease-out",
        "&:hover": onClick ? { backgroundColor: "#f5f5f5" } : undefined,
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box display="flex" alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box mr={2} sx={{ flexShrink: 0 }}>
            <IconComponent fontSize="large" />
          </Box>
          {/* minWidth: 0 is required on a flex child for its text-overflow
              rules below to actually take effect, instead of the box just
              growing to fit the (long) text. */}
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            {/* NEW: fixed-height title area (2 lines worth) so "Total
                Products" (wraps) and "Low Stock" (doesn't) reserve the same
                vertical space either way. */}
            <Typography
              variant="h6"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: "2.6em",
                lineHeight: 1.3,
                transition: "color 0.1s ease-out",
                "&:hover": { color: "#1976d2", fontWeight: "bold" },
              }}
            >
              {title}
            </Typography>

            {/* NEW: Tooltip reveals the exact, uncompacted number on
                hover/tap — nothing is ever silently lost, it's just not
                spelled out in full at all times on a narrow card. */}
            <Tooltip title={`${prefix ?? ""}${targetValue.toLocaleString("en-IN")}`} arrow>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  // NEW: shrink automatically on narrow viewports instead of
                  // a fixed h4 size that only fits desktop-width cards.
                  fontSize: { xs: "1.35rem", sm: "1.6rem", md: "2rem" },
                }}
              >
                {prefix}
                {displayText}
              </Typography>
            </Tooltip>

            <Typography color="textSecondary" noWrap>
              {percentage}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default HeaderCard;